import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import Sentry from 'config/sentry';

import { AppDispatch } from 'containers/Femtasy';

import {
  MeDocument,
  MeQuery,
  UserProfileFragment,
} from 'graphql/generated/graphql';

import cookieStorage from 'helpers/cookieStorage';
import redirectToUserMarketIfNeeded, {
  marketToString,
} from 'helpers/redirectToUserMarketIfNeeded';
import { isActiveSubscription } from 'helpers/subscription';

import { buildGraphaelQueryOptions } from 'hooks/useGraphaelDefaults';
import { apolloClient } from 'hooks/withGraphQL';

import { IS_VERIFIED } from 'store/consts';
import { authLogout } from 'store/modules/auth';
import { GetReduxState } from 'store/ReduxState';

import api from 'api';

import { Screen, setExcludeAlreadyListenedStories } from './algolia_filters';

export interface ProfileState {
  user: UserProfileFragment | undefined;
  isLoading: boolean;
  isVerified: boolean;
  error: any;
  accountConfirmationRequested: boolean;
}

export const initialProfileState: ProfileState = {
  user: undefined,
  isLoading: false,
  isVerified: false,
  error: null,
  accountConfirmationRequested: false,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState: initialProfileState,
  reducers: {
    setProfileUser: (state, action: PayloadAction<UserProfileFragment>) => {
      state.user = action.payload;
    },
    verifyUser: (state) => {
      state.isVerified = true;
    },
    getProfileStart: (state) => {
      // Do nothing, mimics original behavior where the action was dispatched but not handled.
    },
    getProfileError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setAccountConfirmationRequested: (state) => {
      state.accountConfirmationRequested = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authLogout.type, (state) => {
        return {
          ...initialProfileState,
          isVerified: false,
        };
      });
  },
});

export const {
  setProfileUser,
  verifyUser,
  getProfileStart,
  getProfileError,
  setAccountConfirmationRequested: setAccountConfirmationRequestedAction,
} = profileSlice.actions;

export default profileSlice.reducer;

export const setVerified = () => (dispatch: AppDispatch) => {
  cookieStorage.setItem(IS_VERIFIED, JSON.stringify(true));
  dispatch(verifyUser());
};

export const getProfile =
  (user: UserProfileFragment) =>
  (dispatch: AppDispatch, getState: GetReduxState) => {
    const { primaryMarket } = user;
    const { pathname } = window.location;

    const {
      translations: { locale },
    } = getState();

    redirectToUserMarketIfNeeded({
      currentPathname: pathname,
      dispatch,
      urlLocale: locale,
      userMarket: marketToString(primaryMarket),
    });

    dispatch(setProfileUser(user));
  };

export const getUserProfile = () => {
  return async (dispatch: AppDispatch, getState: GetReduxState) => {
    dispatch(getProfileStart());

    const queryOptions = buildGraphaelQueryOptions(
      getState().auth.isAuthenticated,
      getState().auth.headers,
      getState().translations.locale,
    );

    try {
      const { data, errors } = await apolloClient.query<MeQuery>({
        query: MeDocument,
        ...queryOptions,
        fetchPolicy: 'no-cache',
      });

      if (!errors) {
        // Guard: only dispatch profile if user is still authenticated
        // Prevents in-flight requests from repopulating profile after logout
        if (!getState().auth.isAuthenticated) {
          return Promise.resolve(data.me);
        }

        const excludeAlreadyListenedStories = data.userPreferences
          ? data.userPreferences.hideAlreadyListened
          : true;

        // @ts-ignore
        dispatch(getProfile(data.me));

        if (isActiveSubscription(data.me.subscriptionExpiresAt)) {
          dispatch(
            setExcludeAlreadyListenedStories({
              excludeAlreadyListenedStories,
              screen: Screen.SearchResultsScreen,
            }),
          );
          dispatch(
            setExcludeAlreadyListenedStories({
              excludeAlreadyListenedStories,
              screen: Screen.FilterScreen,
            }),
          );
        }

        return Promise.resolve(data.me);
      } else {
        dispatch(getProfileError(errors));

        return Promise.reject(errors);
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  };
};

export function setAccountConfirmationRequested() {
  return (dispatch: AppDispatch) => {
    dispatch(setAccountConfirmationRequestedAction());
    api.auth.requestNewConfirmationLink();
  };
}
